import React, { useEffect, useRef } from "react";
import { RippleButton } from "../../pages/Home/Home";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/logo.png";

const Footer = () => {
    const navigate = useNavigate();
    const footerRef = useRef(null);
    const particlesRef = useRef([]);

    const links = ['Accueil', 'Environnement', 'Agriculture', 'Nutrition'];

    const handleNavigation = (link) => {
        const routes = {
            Accueil: "/",
            Environnement: "/environnement",
            Agriculture: "/agriculture",
            Nutrition: "/nutrition",
        };
        if (routes[link]) navigate(routes[link]);
    };

    useEffect(() => {
        const footer = footerRef.current;
        if (!footer) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    footer.classList.add("footer-visible");
                }
            },
            { threshold: 0.15 }
        );
        observer.observe(footer);
        return () => observer.disconnect();
    }, []);

    return (
        <>
            <style>{`
                .footer-root {
                    background: #0a0804;
                    position: relative;
                    overflow: hidden;
                    padding: 0;
                    font-family: 'DM Sans', sans-serif;
                }

                /* ── Animated grain overlay ── */
                .footer-root::before {
                    content: '';
                    position: absolute;
                    inset: 0;
                    background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='0.04'/%3E%3C/svg%3E");
                    opacity: 0.35;
                    pointer-events: none;
                    z-index: 0;
                }

                /* ── Top glowing divider ── */
                .footer-divider {
                    width: 100%;
                    height: 1px;
                    background: linear-gradient(90deg, transparent, rgba(249,115,22,0.6) 40%, rgba(249,115,22,0.6) 60%, transparent);
                    position: relative;
                    z-index: 1;
                }
                .footer-divider::after {
                    content: '';
                    position: absolute;
                    top: -2px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 60%;
                    height: 5px;
                    background: linear-gradient(90deg, transparent, rgba(249,115,22,0.25), transparent);
                    filter: blur(6px);
                }

                /* ── Radial glow ── */
                .footer-glow {
                    position: absolute;
                    top: -80px;
                    left: 50%;
                    transform: translateX(-50%);
                    width: 700px;
                    height: 400px;
                    border-radius: 50%;
                    background: radial-gradient(ellipse, rgba(249,115,22,0.12) 0%, transparent 70%);
                    pointer-events: none;
                    z-index: 0;
                    animation: pulseGlow 4s ease-in-out infinite;
                }
                @keyframes pulseGlow {
                    0%, 100% { opacity: 0.7; transform: translateX(-50%) scale(1); }
                    50%       { opacity: 1;   transform: translateX(-50%) scale(1.07); }
                }

                /* ── Corner accent lines ── */
                .footer-corner {
                    position: absolute;
                    width: 80px;
                    height: 80px;
                    z-index: 1;
                    opacity: 0.3;
                }
                .footer-corner--tl { top: 24px; left: 24px; border-top: 1px solid #f97316; border-left: 1px solid #f97316; }
                .footer-corner--tr { top: 24px; right: 24px; border-top: 1px solid #f97316; border-right: 1px solid #f97316; }

                /* ── Floating particles ── */
                .footer-particle {
                    position: absolute;
                    border-radius: 50%;
                    pointer-events: none;
                    z-index: 0;
                    animation: floatUp linear infinite;
                    opacity: 0;
                }
                @keyframes floatUp {
                    0%   { transform: translateY(0) scale(1);   opacity: 0; }
                    15%  { opacity: 0.6; }
                    85%  { opacity: 0.3; }
                    100% { transform: translateY(-160px) scale(0.4); opacity: 0; }
                }

                /* ── Main content area ── */
                .footer-inner {
                    position: relative;
                    z-index: 2;
                    padding: 4rem 2rem 2.5rem;
                    max-width: 900px;
                    margin: 0 auto;
                    text-align: center;
                }

                /* ── Logo block ── */
                .footer-logo-wrap {
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 0.75rem;
                    margin-bottom: 2.5rem;
                    opacity: 0;
                    transform: translateY(18px);
                    transition: opacity 0.7s ease, transform 0.7s ease;
                }
                .footer-visible .footer-logo-wrap { opacity: 1; transform: translateY(0); transition-delay: 0.05s; }

                .footer-logo-img {
                    height: 44px;
                    width: auto;
                    filter: drop-shadow(0 0 10px rgba(249,115,22,0.4));
                    transition: filter 0.3s;
                }
                .footer-logo-img:hover {
                    filter: drop-shadow(0 0 18px rgba(249,115,22,0.75));
                }
                .footer-logo-name {
                    font-family: 'Syne', sans-serif;
                    font-weight: 800;
                    font-size: 1.2rem;
                    color: #fff;
                    letter-spacing: 0.03em;
                }
                .footer-logo-name span {
                    color: #f97316;
                }

                /* ── Tagline ── */
                .footer-tagline {
                    opacity: 0;
                    transform: translateY(18px);
                    transition: opacity 0.7s ease, transform 0.7s ease;
                }
                .footer-visible .footer-tagline { opacity: 1; transform: translateY(0); transition-delay: 0.15s; }

                .footer-tagline h2 {
                    font-family: 'Syne', sans-serif;
                    font-weight: 800;
                    font-size: clamp(1.4rem, 3.5vw, 2.2rem);
                    color: #fff;
                    line-height: 1.25;
                    margin: 0 0 0.75rem;
                    letter-spacing: -0.02em;
                }
                .footer-tagline h2 em {
                    font-style: normal;
                    color: #f97316;
                    position: relative;
                }
                .footer-tagline h2 em::after {
                    content: '';
                    position: absolute;
                    left: 0; right: 0; bottom: -2px;
                    height: 2px;
                    background: #f97316;
                    transform: scaleX(0);
                    transform-origin: left;
                    transition: transform 0.5s ease 0.9s;
                }
                .footer-visible .footer-tagline h2 em::after { transform: scaleX(1); }

                .footer-tagline p {
                    font-size: 14px;
                    color: rgba(255,255,255,0.38);
                    max-width: 420px;
                    margin: 0 auto 2.5rem;
                    line-height: 1.7;
                }

                /* ── Separator dot row ── */
                .footer-dots {
                    display: flex;
                    justify-content: center;
                    gap: 6px;
                    margin-bottom: 2rem;
                    opacity: 0;
                    transition: opacity 0.6s ease;
                }
                .footer-visible .footer-dots { opacity: 1; transition-delay: 0.3s; }
                .footer-dot {
                    width: 5px; height: 5px;
                    border-radius: 50%;
                    background: rgba(249,115,22,0.4);
                }
                .footer-dot:nth-child(2) { background: rgba(249,115,22,0.7); transform: scale(1.3); }

                /* ── Nav links ── */
                .footer-nav {
                    display: flex;
                    justify-content: center;
                    gap: 0.25rem;
                    flex-wrap: wrap;
                    margin-bottom: 2.5rem;
                    opacity: 0;
                    transform: translateY(14px);
                    transition: opacity 0.7s ease, transform 0.7s ease;
                }
                .footer-visible .footer-nav { opacity: 1; transform: translateY(0); transition-delay: 0.35s; }

                .footer-nav-btn {
                    position: relative;
                    font-size: 13px;
                    font-weight: 400;
                    color: rgba(255,255,255,0.5);
                    background: none;
                    border: none;
                    cursor: pointer;
                    font-family: 'DM Sans', sans-serif;
                    padding: 0.45rem 1rem;
                    border-radius: 6px;
                    transition: color 0.25s, background 0.25s;
                    letter-spacing: 0.04em;
                    text-transform: uppercase;
                    font-size: 11px;
                }
                .footer-nav-btn::after {
                    content: '';
                    position: absolute;
                    bottom: 4px; left: 50%; right: 50%;
                    height: 1px;
                    background: #f97316;
                    transition: left 0.3s ease, right 0.3s ease;
                }
                .footer-nav-btn:hover {
                    color: #fff;
                    background: rgba(249,115,22,0.08);
                }
                .footer-nav-btn:hover::after { left: 14px; right: 14px; }

                /* ── CTA buttons ── */
                .footer-cta {
                    display: flex;
                    gap: 0.75rem;
                    justify-content: center;
                    flex-wrap: wrap;
                    margin-bottom: 3rem;
                    opacity: 0;
                    transform: translateY(14px);
                    transition: opacity 0.7s ease, transform 0.7s ease;
                }
                .footer-visible .footer-cta { opacity: 1; transform: translateY(0); transition-delay: 0.45s; }

                .footer-btn-primary {
                    padding: 0.85rem 2rem !important;
                    border-radius: 10px !important;
                    background: linear-gradient(135deg, #f97316, #ea580c) !important;
                    color: #fff !important;
                    font-size: 13px !important;
                    font-weight: 600 !important;
                    letter-spacing: 0.03em !important;
                    box-shadow: 0 0 24px rgba(249,115,22,0.35), inset 0 1px 0 rgba(255,255,255,0.15) !important;
                    transition: box-shadow 0.3s, transform 0.2s !important;
                    border: none !important;
                }
                .footer-btn-primary:hover {
                    box-shadow: 0 0 36px rgba(249,115,22,0.6), inset 0 1px 0 rgba(255,255,255,0.2) !important;
                    transform: translateY(-2px) !important;
                }

                .footer-btn-secondary {
                    padding: 0.85rem 2rem !important;
                    border-radius: 10px !important;
                    background: rgba(255,255,255,0.04) !important;
                    color: rgba(255,255,255,0.8) !important;
                    border: 1px solid rgba(255,255,255,0.15) !important;
                    font-size: 13px !important;
                    font-weight: 500 !important;
                    letter-spacing: 0.03em !important;
                    backdrop-filter: blur(4px) !important;
                    transition: background 0.3s, border-color 0.3s, transform 0.2s !important;
                }
                .footer-btn-secondary:hover {
                    background: rgba(255,255,255,0.09) !important;
                    border-color: rgba(249,115,22,0.5) !important;
                    transform: translateY(-2px) !important;
                }

                /* ── Bottom bar ── */
                .footer-bottom {
                    position: relative;
                    z-index: 2;
                    border-top: 1px solid rgba(255,255,255,0.06);
                    padding: 1.2rem 2rem;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    gap: 1.5rem;
                    flex-wrap: wrap;
                    opacity: 0;
                    transition: opacity 0.8s ease;
                }
                .footer-visible .footer-bottom { opacity: 1; transition-delay: 0.6s; }

                .footer-copy {
                    font-size: 11px;
                    color: rgba(255,255,255,0.18);
                    letter-spacing: 0.04em;
                }
                .footer-copy span {
                    color: rgba(249,115,22,0.5);
                }

                .footer-badge {
                    font-size: 10px;
                    color: rgba(255,255,255,0.18);
                    border: 1px solid rgba(255,255,255,0.08);
                    padding: 3px 10px;
                    border-radius: 100px;
                    letter-spacing: 0.06em;
                    text-transform: uppercase;
                }
            `}</style>

            <footer className="footer-root" ref={footerRef}>
                {/* Top divider */}
                <div className="footer-divider" />

                {/* Ambient glow */}
                <div className="footer-glow" />

                {/* Corner accents */}
                <div className="footer-corner footer-corner--tl" />
                <div className="footer-corner footer-corner--tr" />

                {/* Floating particles */}
                {[...Array(8)].map((_, i) => (
                    <div
                        key={i}
                        className="footer-particle"
                        style={{
                            left: `${10 + i * 11}%`,
                            bottom: `${10 + (i % 3) * 12}%`,
                            width: i % 2 === 0 ? 3 : 2,
                            height: i % 2 === 0 ? 3 : 2,
                            background: i % 3 === 0 ? '#f97316' : 'rgba(255,255,255,0.4)',
                            animationDuration: `${4 + i * 0.7}s`,
                            animationDelay: `${i * 0.5}s`,
                        }}
                    />
                ))}

                {/* Main content */}
                <div className="footer-inner">

                    {/* Logo */}
                    <div className="footer-logo-wrap">
                        <img src={logo} alt="Tumaini Letu" className="footer-logo-img" />
                        <span className="footer-logo-name">
                            Tumaini <span>Letu</span>
                        </span>
                    </div>

                    {/* Tagline */}
                    {/* <div className="footer-tagline">
                        <h2>
                            Prenez le contrôle de vos<br />
                            <em>finances</em> dès aujourd'hui
                        </h2>
                        <p>
                            Épargnez, empruntez et faites fructifier votre argent<br />
                            avec Tumaini Letu — la finance à votre portée.
                        </p>
                    </div> */}

                    {/* Decorative dots */}
                    <div className="footer-dots">
                        {[...Array(5)].map((_, i) => <div key={i} className="footer-dot" />)}
                    </div>

                    {/* Nav links */}
                    <nav className="footer-nav">
                        {links.map((l) => (
                            <button
                                key={l}
                                onClick={() => handleNavigation(l)}
                                className="footer-nav-btn"
                            >
                                {l}
                            </button>
                        ))}
                    </nav>

                    {/* CTA buttons */}
                    <div className="footer-cta">
                        <RippleButton className="footer-btn-primary">
                            Se connecter
                        </RippleButton>
                        <RippleButton className="footer-btn-secondary">
                            S'inscrire
                        </RippleButton>
                    </div>
                </div>

                {/* Bottom bar */}
                <div className="footer-bottom">
                    <p className="footer-copy">
                        © {new Date().getFullYear()} <span>Tumaini Letu ASBL</span>. Tous droits réservés.
                    </p>
                    <span className="footer-badge">ASBL · RDC</span>
                </div>
            </footer>
        </>
    );
};

export default Footer;
